import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  removePlatformLogo,
  updatePlatformSettings,
  uploadPlatformLogo,
} from '../api';
import usePlatformSettings from '../branding/usePlatformSettings';
import { DEFAULT_SITE_NAME } from '../branding/PlatformSettingsProvider';
import BrandLogo from './BrandLogo';
import Button from './ui/Button';

// One (label, color picker, hex value, save button) row - reused for the
// main brand color and each of the 5 dedicated per-slot overrides below.
function ColorField({ id, label, value, fallback, dtoKey, onSaved }) {
  const { t } = useTranslation();

  const [color, setColor] = useState(value || fallback);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // value/fallback change out from under this field whenever a sibling
  // save (e.g. Hauptfarbe, which clears this slot's override server-side)
  // triggers a refresh - re-sync so the swatch reflects the new derived
  // color instead of the stale one from mount.
  useEffect(() => {
    setColor(value || fallback);
  }, [value, fallback]);

  async function handleSave(event) {
    event.preventDefault();

    try {
      setIsSaving(true);
      setError('');
      setMessage('');

      const settings = await updatePlatformSettings({
        [dtoKey]: color,
      });

      await onSaved();

      setMessage(t('adminPanel.brandingColorSuccess'));

      return settings;
    } catch (err) {
      console.error(`Failed to update ${dtoKey}:`, err);

      setError(err.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="account-field-row">
      <label htmlFor={id}>{label}</label>

      <div className="admin-branding-color-row">
        <input
          id={id}
          type="color"
          value={color}
          disabled={isSaving}
          onChange={(event) => {
            setColor(event.target.value);
            setMessage('');
            setError('');
          }}
        />

        <span className="admin-branding-color-value">{color}</span>

        <Button
          variant="secondary"
          disabled={isSaving || color === (value || fallback)}
          onClick={handleSave}
        >
          {isSaving
            ? t('adminPanel.brandingSaving')
            : t('adminPanel.brandingColorSave')}
        </Button>
      </div>

      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {message && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}

// The platform's name - the browser tab title. Saving an empty field clears
// it, and the tab falls back to the built-in name.
function SiteNameField({ value, onSaved }) {
  const { t } = useTranslation();

  const [name, setName] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setName(value ?? '');
  }, [value]);

  async function handleSave(event) {
    event.preventDefault();

    try {
      setIsSaving(true);
      setError('');
      setMessage('');

      await updatePlatformSettings({ site_name: name.trim() });
      await onSaved();

      setMessage(t('adminPanel.brandingSiteNameSuccess'));
    } catch (err) {
      console.error('Failed to update site_name:', err);

      setError(err.message || t('adminPanel.errorGeneric'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="account-field-row">
      <label htmlFor="adminBrandSiteName">
        {t('adminPanel.brandingSiteNameLabel')}
      </label>

      <form className="admin-branding-color-row admin-branding-name-row" onSubmit={handleSave}>
        <input
          id="adminBrandSiteName"
          type="text"
          value={name}
          maxLength={80}
          placeholder={DEFAULT_SITE_NAME}
          disabled={isSaving}
          onChange={(event) => {
            setName(event.target.value);
            setMessage('');
            setError('');
          }}
        />

        <Button
          type="submit"
          variant="secondary"
          disabled={isSaving || name.trim() === (value ?? '')}
        >
          {isSaving
            ? t('adminPanel.brandingSaving')
            : t('adminPanel.brandingSiteNameSave')}
        </Button>
      </form>

      <p className="admin-branding-logo-hint">
        {t('adminPanel.brandingSiteNameHint')}
      </p>

      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {message && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{message}</span>
        </div>
      )}
    </div>
  );
}

const DEFAULT_SLOT_COLOR = '#111111';
const DEFAULT_BRAND_COLOR = '#005E90';
const DEFAULT_HEADER_COLOR = '#ffffff';

function AdminBrandingForm() {
  const { t } = useTranslation();
  const {
    primaryColor,
    headerColor,
    menuColor,
    buttonColor,
    headingColor,
    textColor,
    logoUrl,
    siteName,
    refresh,
  } = usePlatformSettings();

  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');
  const fileInputRef = useRef(null);

  async function handleLogoChange(event) {
    const file = event.target.files[0];

    if (!file) {
      return;
    }

    try {
      setIsUploadingLogo(true);
      setLogoError('');

      await uploadPlatformLogo(file);
      await refresh();
    } catch (error) {
      console.error('Failed to upload platform logo:', error);

      setLogoError(
        error.message || t('adminPanel.errorGeneric'),
      );
    } finally {
      setIsUploadingLogo(false);
      event.target.value = '';
    }
  }

  async function handleRemoveLogo() {
    try {
      setIsUploadingLogo(true);
      setLogoError('');

      await removePlatformLogo();
      await refresh();
    } catch (error) {
      console.error('Failed to remove platform logo:', error);

      setLogoError(
        error.message || t('adminPanel.errorGeneric'),
      );
    } finally {
      setIsUploadingLogo(false);
    }
  }

  return (
    <div className="account-form">
      <SiteNameField value={siteName} onSaved={refresh} />

      <div className="account-field-row">
        <label htmlFor="adminBrandLogo">
          {t('adminPanel.brandingLogoLabel')}
        </label>

        <div className="admin-branding-logo-row">
          <div className="admin-branding-logo-preview">
            <BrandLogo variant="mark" />
          </div>

          <input
            ref={fileInputRef}
            id="adminBrandLogo"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={isUploadingLogo}
            onChange={handleLogoChange}
          />

          <Button
            variant="secondary"
            disabled={isUploadingLogo}
            onClick={() => fileInputRef.current.click()}
          >
            {isUploadingLogo
              ? t('adminPanel.brandingSaving')
              : t('adminPanel.brandingLogoUpload')}
          </Button>

          {logoUrl && (
            <Button
              variant="secondary"
              disabled={isUploadingLogo}
              onClick={handleRemoveLogo}
            >
              {t('adminPanel.brandingLogoRemove')}
            </Button>
          )}
        </div>

        <p className="admin-branding-logo-hint">
          {t('adminPanel.brandingLogoHint')}
        </p>

        {logoError && (
          <div
            className="login-message login-message-error"
            role="alert"
          >
            <i className="fa-solid fa-circle-exclamation" />
            <span>{logoError}</span>
          </div>
        )}
      </div>

      <ColorField
        id="adminBrandColor"
        label={t('adminPanel.brandingColorLabel')}
        value={primaryColor}
        fallback={DEFAULT_BRAND_COLOR}
        dtoKey="primary_color"
        onSaved={refresh}
      />

      <ColorField
        id="adminHeaderColor"
        label={t('adminPanel.brandingHeaderColorLabel')}
        value={headerColor}
        fallback={DEFAULT_HEADER_COLOR}
        dtoKey="header_color"
        onSaved={refresh}
      />

      <ColorField
        id="adminMenuColor"
        label={t('adminPanel.brandingMenuColorLabel')}
        value={menuColor}
        fallback={primaryColor}
        dtoKey="menu_color"
        onSaved={refresh}
      />

      <ColorField
        id="adminButtonColor"
        label={t('adminPanel.brandingButtonColorLabel')}
        value={buttonColor}
        fallback={primaryColor}
        dtoKey="button_color"
        onSaved={refresh}
      />

      <ColorField
        id="adminHeadingColor"
        label={t('adminPanel.brandingHeadingColorLabel')}
        value={headingColor}
        fallback={DEFAULT_SLOT_COLOR}
        dtoKey="heading_color"
        onSaved={refresh}
      />

      <ColorField
        id="adminTextColor"
        label={t('adminPanel.brandingTextColorLabel')}
        value={textColor}
        fallback={DEFAULT_SLOT_COLOR}
        dtoKey="text_color"
        onSaved={refresh}
      />
    </div>
  );
}

export default AdminBrandingForm;
